import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import Filter from '@/src/components/organism/Filter';
import { NAV_CONST } from '@/src/constants/nav';
import { recyclingPackageProductFilters } from './filter';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Table from '@/src/components/atom/Table';
import { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from '@tanstack/react-query';
import {
  getRecyclingPackageProductsAPI,
  getRecyclingPackageProductsByIdAPI,
  getRecyclingPackageProductsUnits,
} from '@/src/apis/rest/recyclingProducts';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { recyclingPackageProductListColumn } from './column';
import RecyclingPackageProductDetailModal from './components/RecyclingPackageProductDetailModal';
import RecyclingPackageProductCreateModal from './components/RecyclingPackageProductCreateModal';
import RecyclingPackageProductEditModal from './components/RecyclingPackageProductEditModal';
import useQueryValues from '@/src/hooks/useQueryValues';

type ModalState = 'NONE' | 'CREATE' | 'EDIT' | 'DETAIL';

const RecyclingPackageProduct = () => {
  const { defaultValues } = useQueryValues();

  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);

  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const {
    data: recyclingPackageProductsListData,
    isError,
    isFetching,
    refetch: refetchRecyclingPackageProductList,
  } = useQuery({
    queryKey: ['recycling-package-product', values, page, pageSize],
    queryFn: () => {
      return getRecyclingPackageProductsAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });

  const {
    data: recyclingPackageProductDetailData,
    refetch: refetchRecyclingPackageProductDetailData,
  } = useQuery({
    queryKey: ['recycling-package-product', selectedId],
    queryFn: () => {
      return getRecyclingPackageProductsByIdAPI({
        id: Number(selectedId),
      });
    },
    enabled: !!selectedId,
  });
  const { data: recyclingPackageProductUnits } = useQuery({
    queryKey: ['recycling-package-products-units'],
    queryFn: () => {
      return getRecyclingPackageProductsUnits();
    },
  });

  const handleDetailClick = (id: number) => {
    setSelectedId(id);
    handleOpenModal('DETAIL');
  };

  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };

  const handleCloseModal = () => {
    setModalState('NONE');
  };

  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.recyclingPackageProducts}
        breadcrumbList={[NAV_CONST.recyclingPackageProducts]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={recyclingPackageProductFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">수거 품목 목록</Typo>
              <Button
                buttonSize="medium"
                onClick={() => {
                  handleOpenModal('CREATE');
                }}
              >
                <div className="flex items-center gap-x-2">
                  <LucideIcon name="Plus" color="white" />
                  수거 품목 등록
                </div>
              </Button>
            </div>

            <Table
              ref={gridRef}
              rowData={
                isError ? [] : recyclingPackageProductsListData?.rows || []
              }
              isPaginationDataMapping
              columnDefs={recyclingPackageProductListColumn(e => {
                handleDetailClick(e);
              })}
              total={recyclingPackageProductsListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>

      {recyclingPackageProductDetailData && modalState === 'DETAIL' && (
        <RecyclingPackageProductDetailModal
          open={modalState === 'DETAIL'}
          onOpenChange={e => {
            if (!e) setSelectedId(null);
            handleToggleModal('DETAIL');
          }}
          data={recyclingPackageProductDetailData}
          onOkClick={() => handleOpenModal('EDIT')}
        />
      )}
      {recyclingPackageProductDetailData && modalState === 'EDIT' && (
        <RecyclingPackageProductEditModal
          open={modalState === 'EDIT'}
          onOpenChange={e => {
            handleCloseModal();
          }}
          onOk={() => {
            refetchRecyclingPackageProductList();
            refetchRecyclingPackageProductDetailData();
            setModalState('DETAIL');
          }}
          data={recyclingPackageProductDetailData}
          unitList={recyclingPackageProductUnits}
        />
      )}
      {modalState === 'CREATE' && (
        <RecyclingPackageProductCreateModal
          open={modalState === 'CREATE'}
          onOpenChange={handleCloseModal}
          onOk={refetchRecyclingPackageProductList}
          unitList={recyclingPackageProductUnits}
        />
      )}
    </>
  );
};

export default RecyclingPackageProduct;
